// External dependencies
import { Droppable, Draggable } from '@hello-pangea/dnd';
import cn from 'classnames';
import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InlineEditInput from '@/components/UI/InlineEditInput/InlineEditInput';

import { _get, _isNil, _notNil } from '@/littledash';

// Model types
import type { Animal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { CageCardCollection } from './Cages';

// Support modules
import { ResponseType } from '@/support/ApiService';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { errorToast, successToast } from '@/helpers';
import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { modalAction } from '@/utils/modal';

// Internal components
import CageCard from '@/components/Cages/CageCard';
import CardColumn from '@/components/CardColumn';
import Footer from '@/components/CardColumn/Footer';
import CageCardTemplateWrapper from '@/components/Modals/PrintCageCards/CageCardTemplateWrapper';
import Icon from '@/components/UI/Icon';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList, { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import Tooltip from '@/components/UI/Tooltip';

interface CageCardGroup {
  id: ID;
  title: string;
  duplicateName: boolean;
  items: Array<Animal>;
}

interface CageColumnProps {
  group: CageCardGroup;
  cage: CageCardCollection;
  cages: Array<CageCardCollection>;
  study: Study;
  groupId: ID;
  title: string;
  updateCageName: (cage: CageCardCollection, value: string) => void;
  validateCageName: (name: string) => boolean;
  groupIndex: number;
  toggleAddAnimalsModal: (cage: CageCardCollection) => void;
  toggleDeleteCageModal: (collection: CageCardCollection) => void;
  animalIdProperty: ID;
  fetchCages: () => void;
  inStudyView: boolean;
  dragSourceId: string | null;
}

const CageColumn: FC<CageColumnProps> = ({
  group,
  cage,
  cages,
  study,
  groupId,
  title,
  updateCageName,
  validateCageName,
  groupIndex,
  toggleAddAnimalsModal,
  toggleDeleteCageModal,
  animalIdProperty,
  fetchCages,
  inStudyView,
  dragSourceId,
}) => {
  const MAX_ANIMALS_PER_CAGE = MAX_CAGE_POPULATION;
  const [printCage, setPrintCage] = useState(false);
  let userCanEdit = true;
  if (study) {
    userCanEdit = Auth.isWriteUserForStudy(study);
  }
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const { atunesIntegrationEnabled } = useSelector((state: State) => ({
    atunesIntegrationEnabled: state?.team?.features?.atunes_colony_management_integration ?? false,
  }));

  const { invoke: splitStudyCage } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/cages/{cageApiId}/split',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'cage-column-split-cage' } },
  });

  const handleSplitCage = async (cage: CageCardCollection) => {
    const splitStudyCageResponse = await splitStudyCage({
      path: {
        studyApiId: study.api_id,
        cageApiId: cage.api_id,
      },
    });

    if (splitStudyCageResponse?.type === ResponseType.Success) {
      fetchCages();
      successToast('Cage split successfully');
    } else {
      errorToast('Unable to split cage. Please try again or contact support if the issue persists.');
    }
  };

  const buildActionMenuForCage = (cage: CageCardCollection) => {
    const deleteDisabled = Boolean(_get(cage, 'subjects.data').length);
    const reorderDisabled = !study || cages.length < 2 || !userCanEdit;
    const splitDisabled = _isNil(study?.api_id) || !userCanEdit || !inStudyView;

    const reorderTooltip = () => {
      if (!userCanEdit) {
        return 'You must have at least write access to the study to reorder cages.';
      }
      if (!study) {
        return 'Cage reordering is only available when viewing cages assigned to a study.';
      }
      if (cages.length < 2) {
        return 'At least 2 cages are required for reordering.';
      }
      return undefined;
    };

    const splitTooltip = () => {
      if (!userCanEdit) {
        return 'You must have at least write access to the study to split cages.';
      }
      if (!inStudyView) {
        return 'Cage splitting is only available when viewing cages within a study.';
      }
      return undefined;
    };

    const actions: ActionProps[] = [
      {
        name: 'Print label',
        action: () => setPrintCage(true),
      },
      {
        name: 'Reorder',
        disabled: reorderDisabled,
        action: () => {
          if (!reorderDisabled) {
            const selectOptions = cages.reduce<Array<{ value: string; label: string }>>((acc, c) => {
              if (c.api_id !== cage.api_id) {
                acc.push({
                  value: c.api_id,
                  label: c.name,
                });
              }
              return acc;
            }, []);

            openModal('REORDER_CAGE', {
              cage,
              selectOptions,
              studyApiId: study?.api_id ?? '',
              fetchCages,
            });
          }
        },
        tooltip: reorderTooltip(),
      },
      {
        name: 'Split cage',
        disabled: splitDisabled,
        action: () => handleSplitCage(cage),
        tooltip: splitTooltip(),
      },
    ];

    actions.push(
      {
        name: 'Rename',
        disabled: !userCanEdit,
        action: () => {
          const element = document?.getElementById(`editable_${cage.id}`)?.childNodes?.[0] as HTMLElement;
          element?.click();
        },
      },
      {
        name: 'Delete',
        disabled: !userCanEdit || deleteDisabled,
        action: () => {
          toggleDeleteCageModal(cage);
        },
        className: 'red',
        tooltip: deleteDisabled ? "Cages can't be deleted if animals exist within them" : '',
      }
    );

    return actions;
  };

  const hasSpace = MAX_ANIMALS_PER_CAGE > group.items.length;
  const hasDuplicateName = group?.duplicateName ?? false;

  const findStudyGroup = (subject: Animal): TreatmentGroup | undefined => {
    if (!study) {
      return undefined;
    }
    return study.study_groups.find((g) => parseInt(String(g.id)) === parseInt(String(subject.study_group_id)));
  };

  const columnTitle = (
    <div className="flex">
      {hasDuplicateName && (
        <div className="flex" style={{ marginTop: '9px' }}>
          <Tooltip render="Duplicate cage name">
            <Icon icon="critical_alert" className="dark-red" width="22" height="22" />
          </Tooltip>
        </div>
      )}
      <div
        data-test-component="Cage-rename-input"
        className={cn('near-black normal f6', { mt2: hasDuplicateName })}
        id={`editable_${groupId}`}
      >
        <InlineEditInput
          value={title}
          change={(value: Record<string, string>) => updateCageName(cage, value.text)}
          propName="text"
          validate={validateCageName}
          classInvalid="ui__error"
          className="ui__editable"
          isDisabled={!userCanEdit}
        />
      </div>
    </div>
  );

  const columnFooter =
    userCanEdit && !atunesIntegrationEnabled ? (
      <Footer text="Add animal" onClick={() => toggleAddAnimalsModal(cage)} />
    ) : null;

  const columnActionMenu = (
    <SelectDropDown
      title="Show action menu"
      iconOnly
      className="hover-near-black"
      alignMenu="right"
      portalProps={{ width: 120 }}
    >
      <ActionList actions={cage && buildActionMenuForCage(cage)} />
    </SelectDropDown>
  );

  const columnPlaceholderCard = (
    <CageCard
      subject={{ name: 'placeholder-subject', dob: '2000-01-01', sex: 'm' } as Animal}
      studyGroup={{ name: 'placeholder-group' } as TreatmentGroup}
      inStudyView={inStudyView}
    />
  );

  const validItems = (group?.items.filter(_notNil) ?? []).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  return (
    <>
      <Droppable droppableId={String(groupIndex)} isDropDisabled={!hasSpace || dragSourceId === String(groupIndex)}>
        {(provided, snapshot) => (
          <CardColumn
            innerRef={provided.innerRef}
            droppableProps={provided.droppableProps}
            isOver={snapshot.isDraggingOver}
            title={columnTitle}
            footer={columnFooter}
            hasSpace={hasSpace}
            actionMenu={columnActionMenu}
            placeholderCard={columnPlaceholderCard}
            droppablePlaceholder={provided.placeholder}
          >
            {validItems.map((subject, itemIndex) => {
              const studyGroup = findStudyGroup(subject);
              const name = _get(subject, animalIdProperty, subject?.name);

              return (
                <Draggable
                  key={subject.id}
                  draggableId={String(subject.id)}
                  index={itemIndex}
                  isDragDisabled={!userCanEdit}
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={{ ...dragProvided.draggableProps.style, cursor: userCanEdit ? 'move' : 'default' }}
                    >
                      <CageCard
                        subject={{ ...subject, name }}
                        studyGroup={studyGroup}
                        inStudyView={inStudyView}
                        isDragging={dragSnapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
          </CardColumn>
        )}
      </Droppable>
      {printCage && <CageCardTemplateWrapper cageId={cage.id} onComplete={() => setPrintCage(false)} />}
    </>
  );
};

export default CageColumn;
